import { useMemo } from 'react';

import { useGalleryContext } from '../../..';
import { identitySelector } from '../../../selectors';
import {
  findDocumentationOnLevelsAndSites,
  findLevelsForSiteAndDocumentation,
  filterLevelsPerSiteByDocumentation,
} from 'shared/domain/documentation/findDocumentationOnLevels';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { LevelOnView } from 'shared/domain/level/types/view';
import { useLevels, useLevelsToSelect } from 'store/level';
import { useSites } from 'store/site';

interface InitialValues {
  site: LabelledEntity;
  level?: LabelledEntity;
}

export interface CreateIssueButtonData {
  show: boolean;
  availableSites: LabelledEntity[];
  availableLevelsPerSite?: HashMap<LevelOnView[]>;
  initialValues?: InitialValues;
  loadingLevels: boolean;
}

/**
 * Hook to check if create issue button should be shown and compute available sites
 * Returns both the visibility flag and the computed availableSites to avoid duplicate calculations
 */
export function useShowCreateIssueButton(): CreateIssueButtonData {
  const [file] = useGalleryContext().useMainDisplayedFile(identitySelector);
  const { items: levels, loading: loadingLevels } = useLevels();
  const { items: sites } = useSites();
  const { levelsPerSite } = useLevelsToSelect();

  const availableSites = useMemo(() => {
    if (!file?.documentationId) {
      return [];
    }

    const { sitesValue } = findDocumentationOnLevelsAndSites(
      levels,
      sites,
      file.documentationId,
      file.versionId
    );

    return sitesValue;
  }, [file, levels, sites]);

  const availableLevelsPerSite = useMemo(() => {
    if (!file?.documentationId) {
      return undefined;
    }

    return filterLevelsPerSiteByDocumentation(
      levels,
      levelsPerSite,
      file.documentationId,
      file.versionId
    );
  }, [file, levels, levelsPerSite]);

  const initialValues = useMemo<InitialValues | undefined>(() => {
    if (!file?.documentationId || availableSites.length !== 1) {
      return undefined;
    }

    const [singleSite] = availableSites;

    const levelsForSite = findLevelsForSiteAndDocumentation(
      levels,
      singleSite._id,
      file.documentationId,
      file.versionId
    );

    const [singleLevel] = levelsForSite;

    return {
      site: singleSite,
      level: levelsForSite.length === 1 ? singleLevel : undefined,
    };
  }, [file, levels, availableSites]);

  return {
    show: Boolean(file?.documentationId && availableSites.length > 0),
    availableSites,
    availableLevelsPerSite,
    initialValues,
    loadingLevels,
  };
}
